import { apiSlice } from '@/shared/api/apiSlice'
import { ServerResponse } from '@/shared/types/common'

export interface TradeinDevice {
	CODE: string
	COUNT: number
	NAME: string
}

export type TradeInResponse = Record<string, Omit<TradeinDevice, 'CODE'>>

export interface TradeInProductData {
	KOD: string
	TITLE: string
	TRADEIN_LINEYKA: string
	VSTROENNAYA_PAMYAT: string
	TRADEIN_TYPE_SIM: string
	TRADEIN_SERIYA_PROTSESSORA: string
	TRADEIN_COLOR: string
	DIAGONAL: string
	PRICE: number
}

export interface PriceItem {
	KOD: string
	VSTROENNAYA_PAMYAT: string
	PRICE: number
	TRADEIN_SERIES: string[]
	TRADEIN_GOD_RELIZA: string
	SECTION_CODE: string
	TRADEIN_LINEYKA: string[]
	TRADEIN_POKOLENIE: string
	MEMORY_SIZE: string
	TRADEIN_PROIZVODITEL_PROTSESSORA: string[]
	DIAGONAL: string
	TITLE: string
	ID: string
	TRADEIN_SERIYA_PROTSESSORA: string[]
	TRADEIN_RAZMER_KORPUSA: string
	ADDITIONAL_DISCOUNT: number
}

export interface PriceResponse {
	ITEMS: PriceItem[]
}

/** Результат расчёта трейд-ин: цена выкупа + гарантированная выгода по акции. */
export interface TradeInPriceResult {
	PRICE: number
	ADDITIONAL_DISCOUNT: number
}

export interface ResponseTProperties {
	PROPERTIES: TProperties[]
}

export interface TProperties {
	ID: number
	CODE: string
	PROPERTY_TYPE: string
	DISPLAY_TYPE: string
	NAME: string
	DISPLAY_EXPANDED: boolean
	FILTER_HINT: string
	SORT: number
	VALUES: Values[]
}

export interface Values {
	ID: string
	ELEMENT_COUNT: number
	SORT: number
	CHECKED: boolean
	VALUE: string
	FILTERED_VALUE?: number
	NAME?: string
}

/** Сортировка свойств фильтрации устройств по полю SORT. */
export const sortTradeInProperties = (properties: TProperties[]): TProperties[] =>
	[...properties].sort((a, b) => a.SORT - b.SORT)

/**
 * Определяет, доступен ли селект для взаимодействия.
 * Активны: первый шаг, уже выбранные свойства и один следующий шаг.
 */
export const isTradeInSelectEnabled = (
	property: TProperties,
	index: number,
	sortedProperties: TProperties[],
	values: Record<string, string> | null
): boolean => {
	if (values?.[property.CODE]) {
		return true
	}

	if (index === 0) {
		return true
	}

	const allPreviousFilled = sortedProperties.slice(0, index).every(p => values?.[p.CODE])

	if (!allPreviousFilled) {
		return false
	}

	const nextStepIndex = sortedProperties.findIndex(p => !values?.[p.CODE])

	return index === nextStepIndex
}

/**
 * Фильтр для запроса опций конкретного селекта.
 * Берём только значения предыдущих свойств.
 */
export const getPartialTradeInFilterParams = (
	sortedProperties: TProperties[],
	values: Record<string, string> | null,
	propertyIndex: number
): Record<string, string> | null => {
	if (propertyIndex === 0) {
		return null
	}

	if (!values) {
		return null
	}

	const partial: Record<string, string> = {}

	for (let i = 0; i < propertyIndex; i++) {
		const code = sortedProperties[i].CODE

		if (values[code]) {
			partial[code] = values[code]
		}
	}

	return Object.keys(partial).length > 0 ? partial : null
}

/**
 * Формирует опции селекта.
 * Для первого свойства с уже выбранным значением показываем все варианты.
 * Для остальных — только значения с ненулевым ELEMENT_COUNT.
 */
export const getTradeInSelectOptions = (
	property: TProperties,
	propertyIndex: number,
	values: Record<string, string> | null
): { label: string; value: string }[] => {
	const hasValue = Boolean(values?.[property.CODE])
	const showAllOptions = propertyIndex === 0 && hasValue
	const items = showAllOptions ? property.VALUES : property.VALUES.filter(item => item.ELEMENT_COUNT !== 0)

	return items.map(value => ({
		label: value.VALUE,
		value: `${property.CODE}=${value.VALUE}`
	}))
}

/** При изменении свойства сбрасывает все последующие (SORT больше изменённого). */
export const getValuesAfterPropertyChange = (
	properties: TProperties[],
	changedCode: string,
	changedValue: string,
	currentValues: Record<string, string> | null
): Record<string, string> => {
	const changedProperty = properties.find(property => property.CODE === changedCode)
	const newValues = { ...(currentValues ?? {}), [changedCode]: changedValue }

	if (!changedProperty) {
		return newValues
	}

	for (const property of properties) {
		if (property.SORT > changedProperty.SORT) {
			delete newValues[property.CODE]
		}
	}

	return newValues
}

export const createFilterString = (obj: { [key: string]: string }) => {
	const filterObj = {}
	for (const key in obj) {
		filterObj[key] = { type: '=', value: [obj[key]] }
	}
	return JSON.stringify(filterObj)
}

export const tradeInApi = apiSlice.injectEndpoints({
	// Позволяет Fast Refresh подхватывать правки эндпоинтов (query/transformResponse) без полного рестарта dev-сервера
	overrideExisting: true,
	endpoints: builder => ({
		getCategories: builder.query<TProperties[], { category: string; params?: { [key: string]: string } }>({
			query: ({ category = 'iphone', params = null }) => {
				if (!params) {
					return { url: `index/trade-in/filter/${category}` }
				}
				return {
					url: `index/trade-in/filter/${category}`,
					params: { filter: createFilterString(params) }
				}
			},
			transformResponse: (response: ServerResponse<ResponseTProperties>) =>
				response.data.PROPERTIES.filter(item => item.CODE !== 'PRICE')
		}),
		getProductTradeInData: builder.query<TradeInProductData, number>({
			query: productId => `products/${productId}/trade-in`,
			transformResponse: (response: ServerResponse<TradeInProductData>) => response.data
		}),
		getBaseCategories: builder.query<TradeinDevice[], void>({
			query: () => `index/trade-in/products/categories`,
			transformResponse: (response: ServerResponse<TradeInResponse>) =>
				Object.entries(response.data).map(([code, item]) => ({ CODE: code, ...item }))
		}),
		getTradeInPrice: builder.query<TradeInPriceResult, { [key: string]: string }>({
			query: params => {
				if (!params) {
					return { url: `index/trade-in/products` }
				}
				return {
					url: `index/trade-in/products`,
					params: { filter: createFilterString(params) }
				}
			},
			transformResponse: (res: ServerResponse<PriceResponse>) => ({
				PRICE: res.data.ITEMS[0].PRICE,
				ADDITIONAL_DISCOUNT: res.data.ITEMS[0].ADDITIONAL_DISCOUNT ?? 0
			})
		}),
		getModels: builder.query<TProperties, { category: string }>({
			query: ({ category = 'iphone' }) => ({
				url: `index/trade-in/filter/${category}`
			}),
			transformResponse: (response: ServerResponse<ResponseTProperties>) =>
				response.data.PROPERTIES.find(item => item.CODE === 'TRADEIN_LINEYKA') || {
					ID: 0,
					CODE: 'TRADEIN_LINEYKA',
					PROPERTY_TYPE: 'L',
					DISPLAY_TYPE: 'F',
					NAME: 'Линейка',
					DISPLAY_EXPANDED: true,
					FILTER_HINT: '',
					SORT: 2,
					VALUES: []
				}
		}),
		getMemory: builder.query<TProperties, { category: string; model: string }>({
			query: ({ category = 'iphone', model }) => ({
				url: `index/trade-in/filter/${category}`,
				params: { filter: createFilterString({ TRADEIN_LINEYKA: model }) }
			}),
			transformResponse: (response: ServerResponse<ResponseTProperties>) =>
				response.data.PROPERTIES.find(item => item.CODE === 'VSTROENNAYA_PAMYAT') || {
					ID: 0,
					CODE: 'VSTROENNAYA_PAMYAT',
					PROPERTY_TYPE: 'S',
					DISPLAY_TYPE: 'F',
					NAME: 'Встроенная память',
					DISPLAY_EXPANDED: true,
					FILTER_HINT: '',
					SORT: 3,
					VALUES: []
				}
		}),
		getSimTypes: builder.query<TProperties, { category: string; model: string; memory: string }>({
			query: ({ category = 'iphone', model, memory }) => ({
				url: `index/trade-in/filter/${category}`,
				params: {
					filter: createFilterString({ TRADEIN_LINEYKA: model, VSTROENNAYA_PAMYAT: memory })
				}
			}),
			transformResponse: (response: ServerResponse<ResponseTProperties>) =>
				response.data.PROPERTIES.find(item => item.CODE === 'TRADEIN_TYPE_SIM') || {
					ID: 0,
					CODE: 'TRADEIN_TYPE_SIM',
					PROPERTY_TYPE: 'L',
					DISPLAY_TYPE: 'F',
					NAME: 'Тип сим карты',
					DISPLAY_EXPANDED: true,
					FILTER_HINT: '',
					SORT: 500,
					VALUES: []
				}
		})
	})
})

export const {
	useGetCategoriesQuery,
	useGetBaseCategoriesQuery,
	useLazyGetTradeInPriceQuery,
	useGetProductTradeInDataQuery,
	useGetModelsQuery,
	useGetMemoryQuery,
	useGetSimTypesQuery
} = tradeInApi
